import React from 'react';

import type { ISegmentedToggleProps } from './SegmentedToggle.types';
import { Text } from '../Text/Text';
import { Icon } from '../Icon/Icon';
import { useSlidingPill } from '../../hooks/useSlidingPill';

/**
 * A pill toggle: one brand-filled segment sliding along a bordered track.
 *
 * The app's segmented control, extracted from ModeToggleSwitch — which now
 * renders through it — so the mode switcher and every later toggle share one
 * set of tokens. The track is `bg-bg-primary` inside `border-border-primary`;
 * the selection is a single `bg-bg-brand-solid` pill that slides between
 * segments, matching the tab groups on the dashboard, the CAB panel and the
 * collaboration modal. Never a filled Button: `variant="secondary"` is a solid
 * slate block, which reads as a pressed button rather than a chosen segment.
 *
 * Generic over the value so callers keep their own union or enum rather than
 * casting through strings.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  className = '',
  disabled = false,
  fullWidth = false,
  ariaLabel = 'Toggle',
}: ISegmentedToggleProps<T>) {
  const { containerRef, registerTab, rect, animate } = useSlidingPill(value);

  return (
    <div
      ref={containerRef}
      className={`relative bg-bg-primary border border-border-primary shadow-bg-v2-xs rounded-bg-v2-full p-bg-v2-xs gap-bg-v2-xs flex ${className}`}
      role="group"
      aria-label={ariaLabel}
    >
      {/* Single pill that slides between segments, instead of each segment
          painting its own background on selection. */}
      {rect && (
        <span
          aria-hidden="true"
          className={`absolute rounded-bg-v2-full bg-bg-brand-solid pointer-events-none ${
            animate ? 'transition-all duration-300 ease-out motion-reduce:transition-none' : ''
          }`}
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
        />
      )}
      {options.map((option) => {
        const active = option.value === value;
        // A disabled segment stays visible and keeps its label: it is part of
        // the choice being described, and hiding it would make the control
        // change shape as the form fills in.
        const off = disabled || option.disabled;
        return (
          <button
            key={option.value}
            ref={registerTab(option.value)}
            type="button"
            // Re-picking the current segment is not a change; firing onChange
            // for it makes callers guard against their own no-ops.
            onClick={() => !off && !active && onChange(option.value)}
            disabled={off}
            title={option.title}
            className={`
              relative z-10
              rounded-bg-v2-full px-bg-v2-lg py-bg-v2-md
              flex items-center gap-bg-v2-xs
              transition-colors duration-200 ease-in-out
              cursor-pointer
              border-none outline-none
              bg-transparent
              ${fullWidth ? 'flex-1 justify-center' : ''}
              ${off
                ? 'opacity-50 cursor-not-allowed'
                /* Hover only on inactive segments — a hover fill on the active
                   one would override its brand pill, and clicking it is a no-op. */
                : !active ? 'hover:bg-bg-secondary' : ''}
            `}
            aria-pressed={active}
          >
            {option.icon && (
              <Icon
                name={option.icon}
                size="sm"
                className={active ? '!text-text-primary-on-brand' : 'text-text-tertiary-hover'}
                inheritColor
              />
            )}
            <Text
              variant="text-sm"
              className={active ? '!text-text-primary-on-brand' : 'text-text-tertiary-hover'}
              weight="semibold"
              as="span"
            >
              {option.label}
            </Text>
          </button>
        );
      })}
    </div>
  );
}
